import "./create-flag-modal.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { mergeDateTime } from "~/shared/utils/time-utils.ts";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import { FlagForm } from "./flag-form.tsx";
import { usePostNewFlag } from "./hooks/use-post-create-flag.ts";
import type { FlagFormValues } from "./utils/validation.ts";
import { INITIAL_VALUES, validate } from "./utils/validation.ts";

export const CreateAttentionFlagModal = ({
	onClose,
	isOpen,
	shipment,
}: {
	onClose: () => void;
	isOpen: boolean;
	shipment: AugmentedShipment;
}) => {
	const reasonId = useId();
	const followupDateId = useId();
	const params = useParams();
	const { postNewFlagAsync, isPostingNewFlag } = usePostNewFlag();
	const errorId = `${reasonId}_error`;
	const followupDateErrorId = `${followupDateId}_error`;
	const followupTimeId = useId();
	const followupTimeErrorId = `${followupTimeId}_error`;

	const [values, setValues] = useState<FlagFormValues>(INITIAL_VALUES);
	const [submitted, setSubmitted] = useState(false);

	// Derived during render — single source of truth, no stale error state.
	const errors = validate(values);
	const isValid =
		!errors.description && !errors.followupDate && !errors.followupTime;
	const showFollowupError =
		submitted && (Boolean(errors.followupDate) || Boolean(errors.followupTime));
	const showDescriptionError = submitted && Boolean(errors.description);

	// Returning null doesn't unmount us, so reset state on every close path.
	const handleClose = () => {
		setValues(INITIAL_VALUES);
		setSubmitted(false);
		onClose();
	};

	const handleConfirm = async () => {
		setSubmitted(true);
		const { followupDate, followupTime } = values;
		if (!isValid || !followupDate || !followupTime) {
			return;
		}
		const mergedDate = mergeDateTime(followupDate, followupTime);
		if (!mergedDate) {
			return;
		}
		try {
			await postNewFlagAsync({
				customerId: String(shipment?.customer?.id ?? ""),
				message: values.description,
				poNumber: params["poNumber"] ?? "",
				date: mergedDate,
			});
		} catch {
			// Error toast is surfaced by the hook's onError.
			return;
		}
		handleClose();
	};

	const isSaving = isPostingNewFlag;

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={() => void handleConfirm()}
			showButtonsDisabled={isSaving}
			title={`Create Attention Flag`}
			type={"confirmation"}
			width={"30%"}
		>
			{isSaving ? (
				<div className={"flag-loader"}>
					<Loader
						size={"large"}
						themeColor={"primary"}
						type={"infinite-spinner"}
					/>
				</div>
			) : (
				<FlagForm
					errorId={errorId}
					errors={errors}
					followupDateErrorId={followupDateErrorId}
					followupDateId={followupDateId}
					followupTimeErrorId={followupTimeErrorId}
					followupTimeId={followupTimeId}
					reasonId={reasonId}
					setValues={setValues}
					showDescriptionError={showDescriptionError}
					showFollowupError={showFollowupError}
					values={values}
				/>
			)}
		</TraxModal>
	);
};
